import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, MessageCircle } from "lucide-react";
import { submitBooking, verifyBooking } from "@/lib/api";
import { useSite } from "@/context/SiteContent";
import { CheckoutButton } from "./CheckoutButton";

const ease = [0.2, 0.7, 0.2, 1];
const stepAnim = { initial: { opacity: 0, x: 26 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -26 }, transition: { duration: 0.3, ease } };
const UNLOCK_KEY = "c2c_booking_unlock";
const WHATSAPP_URL = `https://wa.me/6597600315?text=${encodeURIComponent("Hi Scott, I've got the eBook and I'd like to book a 5Ps Conversation.")}`;

const WhatsAppBook = ({ testId }) => (
  <a
    href={WHATSAPP_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testId}
    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem", width: "100%", padding: "0.85rem 1rem", borderRadius: "999px", background: "#25D366", color: "#0b2c25", fontWeight: 700, textDecoration: "none" }}
  >
    <MessageCircle size={18} /> Book instantly on WhatsApp
  </a>
);

const initial = { fullName: "", contactNumber: "", email: "", residency: "", preferredDate: "", preferredTime: "", discussionTopic: "", consent: false };
const minBookingISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 2); // 48-hour minimum notice
  while (d.getDay() === 0 || d.getDay() === 6) d.setDate(d.getDate() + 1); // roll to next weekday
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 10);
};
const isWeekday = (iso) => { const day = new Date(`${iso}T00:00:00`).getDay(); return day >= 1 && day <= 5; };
const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const persist = (token, email) => { try { localStorage.setItem(UNLOCK_KEY, JSON.stringify({ token: token || "", email: email || "" })); } catch { /* ignore */ } };

export const Booking = () => {
  const { timeSlots, price } = useSite();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [unlocked, setUnlocked] = useState(false);
  const [token, setToken] = useState("");
  const [unlockEmail, setUnlockEmail] = useState("");
  const [checking, setChecking] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  useEffect(() => {
    const t = new URLSearchParams(window.location.search).get("book");
    let stored = null;
    try { stored = JSON.parse(localStorage.getItem(UNLOCK_KEY) || "null"); } catch { /* ignore */ }
    const tryToken = t || stored?.token;
    const tryEmail = stored?.email;
    if (!tryToken && !tryEmail) return;
    verifyBooking({ token: tryToken || undefined, email: tryEmail || undefined }).then((r) => {
      if (r.unlocked) {
        setUnlocked(true);
        setToken(tryToken || "");
        const em = r.email || tryEmail;
        if (em) setForm((f) => ({ ...f, email: em }));
        persist(tryToken || "", em || "");
      }
    }).catch(() => {});
  }, []);

  const unlockByEmail = async (e) => {
    e.preventDefault();
    if (!emailOk(unlockEmail.trim())) return setError("Please enter the email you purchased with.");
    setError(""); setChecking(true);
    try {
      const r = await verifyBooking({ email: unlockEmail.trim() });
      if (r.unlocked) { setUnlocked(true); setForm((f) => ({ ...f, email: unlockEmail.trim() })); persist("", unlockEmail.trim()); }
      else setError("We couldn't find a completed purchase for that email. Please use the email you paid with, or get the eBook below.");
    } catch { setError("Could not verify right now. Please try again shortly."); }
    finally { setChecking(false); }
  };

  const next = () => {
    if (!form.fullName.trim() || !form.contactNumber.trim() || !form.email.trim() || !form.residency) return setError("Please complete your name, contact number, email address and residency status.");
    if (!emailOk(form.email.trim())) return setError("Please enter a valid email address.");
    setError(""); setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.preferredDate || !form.preferredTime || !form.consent) return setError("Please choose your preferred date and time, and confirm the contact consent.");
    if (form.preferredDate < minBookingISO()) return setError("Please choose a date at least 48 hours ahead (Mon to Fri).");
    if (!isWeekday(form.preferredDate)) return setError("5Ps Conversations run Monday to Friday. Please pick a weekday.");
    setError(""); setBusy(true);
    try {
      const data = await submitBooking({ ...form, fullName: form.fullName.trim(), contactNumber: form.contactNumber.trim(), email: form.email.trim(), discussionTopic: form.discussionTopic.trim(), purchaseToken: token || undefined });
      setResult(data);
    } catch (err) {
      setError(err?.response?.status === 403 ? "This conversation is for readers. Please get the eBook, then book with the email you paid with." : "We could not submit your booking right now. Please try again shortly.");
    } finally { setBusy(false); }
  };

  const reset = () => { setForm((f) => ({ ...initial, email: f.email })); setResult(null); setStep(1); setError(""); };

  return (
    <section className="booking-section" data-rail="Book Scott" id="booking" data-testid="booking-section">
      <div className="booking-aura" />
      <div className="container booking-grid">
        <div className="booking-copy reveal">
          <div className="eyebrow">A perk for readers</div>
          <h2>Book a 5Ps Conversation with Scott Tang.</h2>
          <p className="lead">Every copy of the eBook comes with the option of a private one-to-one 5Ps Conversation with Scott. Get the eBook, then book a time to talk through your career, first paycheck, priorities and where you want to go next.</p>
          <div className="booking-points">
            <div><span>01</span><div><strong>Get the eBook</strong><p>Your S$6.99 purchase unlocks the conversation.</p></div></div>
            <div><span>02</span><div><strong>Choose your preferred timing</strong><p>Pick a date and time that works best for you.</p></div></div>
            <div><span>03</span><div><strong>Come with one meaningful question</strong><p>Scott uses the 5Ps to explore what matters most to you.</p></div></div>
          </div>
          <div className="booking-trust">
            <span>✓ Included with your eBook purchase</span><span>✓ One-to-one time with Scott</span><span>✓ Takes about a minute to book</span>
          </div>
        </div>

        {!unlocked ? (
          <div className="booking-form-card booking-locked reveal" data-testid="booking-locked">
            <div className="booking-lock-badge"><Lock size={22} /></div>
            <span className="booking-kicker">Readers only</span>
            <h3>Unlock a 1:1 with Scott</h3>
            <p className="booking-locked-lead">A private 5Ps Conversation is included when you get the eBook ({price}). Grab your copy, then come back here to book your time.</p>
            <CheckoutButton testId="booking-locked-checkout">Get the eBook • {price}</CheckoutButton>
            <div className="booking-unlock-divider"><span>Already bought the eBook?</span></div>
            <form onSubmit={unlockByEmail} className="booking-unlock-form" data-testid="booking-unlock-form">
              <input type="email" placeholder="Email you paid with" value={unlockEmail} onChange={(e) => setUnlockEmail(e.target.value)} data-testid="booking-unlock-email" />
              <button type="submit" className="btn btn-outline" disabled={checking} data-testid="booking-unlock-btn">{checking ? "Checking..." : "Unlock booking"}</button>
            </form>
            <div className="form-error" data-testid="booking-unlock-error">{error}</div>
            <p className="booking-privacy-note">We only check that a completed purchase exists for that email. Your details stay private.</p>
          </div>
        ) : (
        <div className="booking-form-card reveal show" data-testid="booking-form-card">
          <div className="booking-form-head">
            <div><span className="booking-kicker">5Ps Conversation</span><h3>Book your appointment</h3></div>
            <div className="booking-step-indicator" data-testid="booking-step-indicator"><span data-testid="booking-step-number">{`${result ? 2 : step}/2`}</span></div>
          </div>
          <div className="booking-progress"><motion.i animate={{ width: step === 1 && !result ? "50%" : "100%" }} transition={{ duration: 0.45, ease }} /></div>
          {!result && (
            <div style={{ marginBottom: "1rem" }}>
              <WhatsAppBook testId="booking-whatsapp-btn" />
              <div style={{ textAlign: "center", fontSize: "0.82rem", opacity: 0.6, margin: "0.6rem 0 0" }}>Scott's assistant schedules it with you in chat — or use the form below.</div>
            </div>
          )}
          <form noValidate onSubmit={submit} data-testid="booking-form">
            <AnimatePresence mode="wait" initial={false}>
            {!result && step === 1 && (
              <motion.div key="s1" className="booking-step is-active" data-testid="booking-step-1" {...stepAnim}>
                <div className="booking-step-title">Your contact details</div>
                <div className="form-grid">
                  <label className="form-field form-field-full"><span>Full Name *</span>
                    <input autoComplete="name" name="fullName" placeholder="Your full name" required type="text" value={form.fullName} onChange={set("fullName")} data-testid="booking-name-input" /></label>
                  <label className="form-field"><span>Contact Number *</span>
                    <input autoComplete="tel" name="contactNumber" placeholder="+65 9123 4567" required type="tel" value={form.contactNumber} onChange={set("contactNumber")} data-testid="booking-phone-input" /></label>
                  <label className="form-field"><span>Email Address *</span>
                    <input autoComplete="email" name="email" placeholder="you@email.com" required type="email" value={form.email} onChange={set("email")} data-testid="booking-email-input" /></label>
                </div>
                <fieldset className="residency-field">
                  <legend>Residency Status *</legend>
                  <div className="residency-options">
                    {["Singaporean", "PR", "Expat"].map((r) => (
                      <label key={r}><input name="residency" type="radio" value={r} checked={form.residency === r} onChange={set("residency")} data-testid={`booking-residency-${r.toLowerCase()}`} /><span>{r}</span></label>
                    ))}
                  </div>
                </fieldset>
                <div className="form-error" data-testid="booking-error">{error}</div>
                <button className="btn btn-gold booking-next" type="button" onClick={next} data-testid="booking-next-btn">Choose Appointment Time →</button>
              </motion.div>
            )}
            {!result && step === 2 && (
              <motion.div key="s2" className="booking-step is-active" data-testid="booking-step-2" {...stepAnim}>
                <div className="booking-step-title">Your preferred appointment</div>
                <p data-testid="booking-rules-note" style={{ fontSize: "0.85rem", opacity: 0.72, margin: "0 0 0.9rem" }}>A 45-minute 5Ps Conversation, online or in person, in Singapore time (GMT+8). Available Monday to Friday, 10am to 7pm. Please choose a time at least 48 hours ahead.</p>
                <div className="form-grid">
                  <label className="form-field form-field-full"><span>Preferred Date *</span>
                    <input name="preferredDate" required type="date" min={minBookingISO()} value={form.preferredDate} onChange={set("preferredDate")} data-testid="booking-date-input" /></label>
                  <div className="form-field form-field-full"><span>Preferred Time *</span>
                    <div className="time-chips" role="radiogroup" aria-label="Preferred time" data-testid="booking-time-slots">
                      {timeSlots.map((t, i) => (
                        <button type="button" key={t} role="radio" aria-checked={form.preferredTime === t} className={`time-chip${form.preferredTime === t ? " on" : ""}`} onClick={() => setForm((f) => ({ ...f, preferredTime: t }))} data-testid={`booking-time-chip-${i}`}>{t}</button>
                      ))}
                    </div>
                  </div>
                  <label className="form-field form-field-full"><span>What would you like to discuss? <em>Optional</em></span>
                    <textarea name="discussionTopic" rows={4} placeholder="For example: I just started working and want more clarity on my first paycheck and future plans." value={form.discussionTopic} onChange={set("discussionTopic")} data-testid="booking-topic-input" /></label>
                </div>
                <label className="booking-consent">
                  <input name="consent" type="checkbox" checked={form.consent} onChange={set("consent")} data-testid="booking-consent-checkbox" />
                  <span>I agree to be contacted by TransitionWorks regarding this appointment request. *</span>
                </label>
                <div className="form-error" data-testid="booking-error">{error}</div>
                <div className="booking-form-actions">
                  <button className="booking-back" type="button" onClick={() => { setError(""); setStep(1); }} data-testid="booking-back-btn">← Back</button>
                  <button className="btn btn-gold" type="submit" disabled={busy} data-testid="booking-submit-btn">{busy ? "Submitting..." : "Submit Booking"}</button>
                </div>
              </motion.div>
            )}
            {result && (
              <motion.div key="done" className="booking-success is-active" data-testid="booking-success" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease }}>
                <motion.div className="booking-success-icon" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.15 }}>✓</motion.div>
                <span className="eyebrow">Booking submitted</span>
                <h3>Thanks, <span data-testid="booking-success-name">{result.fullName.split(" ")[0]}</span>.</h3>
                <p>Your appointment request has been captured. Scott or the TransitionWorks team will confirm the timing using the contact details you provided.</p>
                <div className="booking-summary" data-testid="booking-summary">
                  <strong>{result.preferredDate} · {result.preferredTime}</strong><br />
                  {result.fullName} · {result.residency}<br />
                  {result.contactNumber} · {result.email}
                  {result.discussionTopic && <><br /><br />{result.discussionTopic}</>}
                </div>
                <button className="booking-reset" type="button" onClick={reset} data-testid="booking-reset-btn">Make another booking</button>
                <div style={{ marginTop: "1rem", textAlign: "center", fontSize: "0.85rem", opacity: 0.75 }}>
                  Need to lock in or change a time faster? <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="booking-success-whatsapp" style={{ color: "#1c7a4a", fontWeight: 700, textDecoration: "underline" }}>Book on WhatsApp</a>
                </div>
              </motion.div>
            )}
            </AnimatePresence>
          </form>
          <div className="booking-privacy-note">Your contact details are collected only for appointment coordination and follow-up related to your enquiry.</div>
        </div>
        )}
      </div>
    </section>
  );
};
